import type { FormField } from '../interfaces/form.interface';
import { DropdownData, InputType } from '../interfaces/input.interface';

export type ReactiveDropdownOption = {
    label?: string;
    value: unknown;
    raw?: unknown;
    valueAliases?: readonly unknown[];
    group?: string;
    disabled?: boolean;
};

export type ReactiveDropdownLoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export type ReactiveControlValue = string | number | boolean | null | Array<string | number>;

const DEFAULT_DROPDOWN_IDENTITY_KEY = 'id';

const NULLISH_STRING_TOKENS: ReadonlySet<string> = new Set([
    '',
    'null',
    'undefined',
    'nan',
    'false',
]);

const BAD_DISPLAY_TOKENS: ReadonlySet<string> = new Set([
    ...NULLISH_STRING_TOKENS,
    'invalid date',
]);

export class ReactiveFormValueUtils {
    public static normalizeToken(value: string): string {
        return value.trim();
    }

    public static isNullishStringToken(value: string): boolean {
        return NULLISH_STRING_TOKENS.has(value.trim().toLowerCase());
    }

    public static isBadDisplayToken(value: string): boolean {
        return BAD_DISPLAY_TOKENS.has(value.trim().toLowerCase());
    }

    public static hasNonEmptyValue(value: unknown): boolean {
        if (value === null || value === undefined) {
            return false;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();

            return normalized !== '' && !ReactiveFormValueUtils.isNullishStringToken(normalized);
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return true;
    }

    public static normalizeSwitchValue(value: unknown): 0 | 1 {
        if (value === true || value === 1) {
            return 1;
        }

        if (value === false || value === 0 || value === null || value === undefined) {
            return 0;
        }

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            if ([
                '1',
                'true',
                'si',
                'sí',
                'yes',
                'y',
                'on',
                'activo',
                'active',
            ].includes(normalizedValue)) {
                return 1;
            }
        }

        return 0;
    }

    public static splitSerializedIds(value: string): string[] {
        return value
            .split(/[;,]/)
            .map((item) => ReactiveFormValueUtils.normalizeToken(item))
            .filter((item) => item.length > 0 && !ReactiveFormValueUtils.isBadDisplayToken(item));
    }

    public static isRepeatableField<T>(field: FormField<T>): boolean {
        return field.input?.type === InputType.REPEATABLE_GROUP;
    }

    public static isTwoFieldsConcatField<T>(field: FormField<T>): boolean {
        return field.input?.type === InputType.TWO_FIELDS_CONCAT;
    }

    public static isMultiSelectField<T>(field: FormField<T>): boolean {
        return field.input?.type === InputType.MULTISELECT || field.input?.type === InputType.MULTISELECT_SEARCH;
    }

    public static isSingleDropdownField<T>(field: FormField<T>): boolean {
        return (
            field.input?.type === InputType.DROPDOWN ||
            field.input?.type === InputType.DROPDOWN_SEARCH ||
            field.input?.type === InputType.SELECT_BUTTON ||
            Boolean(field.input?.dropdownConfiguration?.valuePrimaryKey || field.input?.dropdownConfiguration?.controlValueKey)
        ) && !ReactiveFormValueUtils.isMultiSelectField(field);
    }

    public static getFieldPrimaryKey<T>(field: FormField<T>): string {
        return ReactiveFormValueUtils.getFieldControlValueKey(field);
    }

    public static getFieldControlValueKey<T>(field: FormField<T> | undefined): string {
        const config = field?.input?.dropdownConfiguration;

        return config?.controlValueKey ?? config?.valuePrimaryKey ?? DEFAULT_DROPDOWN_IDENTITY_KEY;
    }

    public static getFieldBackendValueKey<T>(field: FormField<T> | undefined): string {
        const config = field?.input?.dropdownConfiguration;

        return config?.backendValueKey ?? config?.valuePrimaryKey ??
            config?.controlValueKey ?? DEFAULT_DROPDOWN_IDENTITY_KEY;
    }

    public static getFieldRawIdentityKeys<T>(field: FormField<T> | undefined): readonly string[] {
        const config = field?.input?.dropdownConfiguration;

        return ReactiveFormValueUtils.toUniqueIdentityKeys([
            config?.controlValueKey,
            config?.valuePrimaryKey,
            config?.backendValueKey,
            ...(config?.rawIdentityKeys ?? []),
            DEFAULT_DROPDOWN_IDENTITY_KEY,
        ]);
    }

    public static toUniqueIdentityKeys(values: ReadonlyArray<string | undefined>): string[] {
        const keys = new Set<string>();

        for (const value of values) {
            const key = String(value ?? '').trim();

            if (key !== '') {
                keys.add(key);
            }
        }

        return Array.from(keys);
    }

    public static getDropdownOptions(dropdownData?: DropdownData<unknown>): ReactiveDropdownOption[] {
        const filtered = Array.isArray(dropdownData?.filtered)
            ? dropdownData.filtered as ReactiveDropdownOption[]
            : [];
        const values = Array.isArray(dropdownData?.values)
            ? dropdownData.values as ReactiveDropdownOption[]
            : [];
        const options = [...filtered];

        for (const value of values) {
            const alreadyExists = options.some((option) => option === value || option.value === value.value);

            if (!alreadyExists) {
                options.push(value);
            }
        }

        return options;
    }

    public static isFieldReady<T>(field: FormField<T>, formType: number, externalDropdownsReady: boolean): boolean {
        const loadState = ReactiveFormValueUtils.getDropdownLoadState(field, formType, externalDropdownsReady);

        return loadState === 'ready' || loadState === 'empty' || loadState === 'idle';
    }

    public static getDropdownLoadState<T>(
        field: FormField<T>,
        formType: number,
        externalDropdownsReady: boolean,
    ): ReactiveDropdownLoadState {
        const isReadOnlyForm = formType >= 3 && formType !== 6;

        if (isReadOnlyForm || field.input?.isEditable === false) {
            return 'ready';
        }

        const dropdownData = field?.input?.data as DropdownData<unknown> | undefined;

        if (!ReactiveFormValueUtils.isDropdownLikeField(field, dropdownData)) {
            return 'ready';
        }

        if (ReactiveFormValueUtils.hasRealDropdownOptions(dropdownData)) {
            return 'ready';
        }

        if (field.dataSource?.lazy === true && !externalDropdownsReady) {
            return 'idle';
        }

        return externalDropdownsReady ? 'empty' : 'loading';
    }

    public static isDropdownLikeField<T>(field: FormField<T>, dropdownData?: DropdownData<unknown>): boolean {
        return Boolean(
            field.input?.dropdownConfiguration?.valuePrimaryKey ||
            field.input?.dropdownConfiguration?.controlValueKey ||
            Array.isArray(dropdownData?.values) ||
            Array.isArray(dropdownData?.filtered),
        );
    }

    public static hasRealDropdownOptions(dropdownData?: DropdownData<unknown>): boolean {
        const options = ReactiveFormValueUtils.getDropdownOptions(dropdownData);

        return options.some((item) => {
            const label = String(item?.label ?? '').trim();

            return label.length > 0 && label !== '...' && item?.value != null;
        });
    }

    public static resolveDropdownOptionRawValue<TEntity = unknown>(option: { value?: unknown; raw?: unknown }): TEntity | null {
        if (option.raw !== null && option.raw !== undefined) {
            return option.raw as TEntity;
        }

        return option.value !== null && typeof option.value === 'object'
            ? option.value as TEntity
            : null;
    }

    public static normalizeComparableKey(value: string | number): string {
        const normalizedValue = String(value)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();

        if (/^0+\d+$/.test(normalizedValue)) {
            return String(Number.parseInt(normalizedValue, 10));
        }

        return normalizedValue;
    }

    public static createOptionsIndex(
        options: ReadonlyArray<ReactiveDropdownOption>,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): Map<string, ReactiveDropdownOption> {
        const index = new Map<string, ReactiveDropdownOption>();
        const ambiguousKeys = new Set<string>();

        for (const option of options) {
            for (const key of ReactiveFormValueUtils.getOptionComparableKeys(option, primaryKey, identityKeys)) {
                if (ambiguousKeys.has(key)) {
                    continue;
                }

                const existingOption = index.get(key);

                if (!existingOption) {
                    index.set(key, option);
                    continue;
                }

                if (ReactiveFormValueUtils.areOptionsSameCanonicalValue(existingOption, option, primaryKey, identityKeys)) {
                    index.set(key, option);
                    continue;
                }

                index.delete(key);
                ambiguousKeys.add(key);
            }
        }

        return index;
    }

    public static findUniqueOptionByComparableKey(
        id: string | number,
        options: ReadonlyArray<ReactiveDropdownOption>,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): ReactiveDropdownOption | null {
        const lookupKeys = new Set([String(id), ReactiveFormValueUtils.normalizeComparableKey(id)]);
        const matchingOptions = options.filter((option) =>
            ReactiveFormValueUtils.getOptionComparableKeys(option, primaryKey, identityKeys).some((key) => lookupKeys.has(key))
        );
        const uniqueOptionsByCanonicalValue = new Map<string, ReactiveDropdownOption>();

        for (const option of matchingOptions) {
            uniqueOptionsByCanonicalValue.set(
                ReactiveFormValueUtils.getOptionCanonicalComparableValue(option, primaryKey, identityKeys),
                option,
            );
        }

        return uniqueOptionsByCanonicalValue.size === 1
            ? Array.from(uniqueOptionsByCanonicalValue.values())[0] ?? null
            : null;
    }

    public static getOptionComparableKeys(
        option: ReactiveDropdownOption,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): string[] {
        const keys = new Set<string>();

        ReactiveFormValueUtils.addComparableOptionKey(keys, option.value, primaryKey, identityKeys);
        ReactiveFormValueUtils.addComparableOptionKey(keys, option.raw, primaryKey, identityKeys);
        ReactiveFormValueUtils.addComparableTextKey(keys, option.label);

        for (const alias of ReactiveFormValueUtils.getOptionValueAliases(option)) {
            ReactiveFormValueUtils.addComparableOptionKey(keys, alias, primaryKey, identityKeys);
            ReactiveFormValueUtils.addComparableTextKey(keys, alias);
        }

        return Array.from(keys);
    }

    public static mapIdToOptionValue(
        id: string | number,
        options: ReadonlyArray<ReactiveDropdownOption>,
        primaryKey: string,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
        identityKeys: readonly string[] = [],
    ): unknown | null {
        const lookupKey = String(id);
        const normalizedLookupKey = ReactiveFormValueUtils.normalizeComparableKey(id);
        const indexedOption = optionsIndex?.get(lookupKey) ?? optionsIndex?.get(normalizedLookupKey);

        if (indexedOption) {
            return indexedOption.value;
        }

        const found = ReactiveFormValueUtils.findUniqueOptionByComparableKey(id, options, primaryKey, identityKeys);

        return found ? found.value : null;
    }

    private static areOptionsSameCanonicalValue(
        first: ReactiveDropdownOption,
        second: ReactiveDropdownOption,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): boolean {
        return ReactiveFormValueUtils.getOptionCanonicalComparableValue(first, primaryKey, identityKeys)
            === ReactiveFormValueUtils.getOptionCanonicalComparableValue(second, primaryKey, identityKeys);
    }

    private static getOptionCanonicalComparableValue(
        option: ReactiveDropdownOption,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): string {
        const comparableValue = ReactiveFormValueUtils.extractComparableId(option.value, primaryKey, identityKeys);

        if (comparableValue !== null) {
            return String(comparableValue);
        }

        return ReactiveFormValueUtils.toComparableValue(option.value, primaryKey, identityKeys);
    }

    private static addComparableOptionKey(
        keys: Set<string>,
        value: unknown,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): void {
        for (const comparableId of ReactiveFormValueUtils.extractComparableIds(value, primaryKey, identityKeys)) {
            const key = String(comparableId);

            keys.add(key);
            keys.add(ReactiveFormValueUtils.normalizeComparableKey(key));
        }
    }

    private static addComparableTextKey(keys: Set<string>, value: unknown): void {
        if (value === null || value === undefined || typeof value === 'object') {
            return;
        }

        const text = String(value).trim();

        if (text === '' || ReactiveFormValueUtils.isBadDisplayToken(text)) {
            return;
        }

        keys.add(text);
        keys.add(ReactiveFormValueUtils.normalizeComparableKey(text));
    }

    private static getOptionValueAliases(option: ReactiveDropdownOption): readonly unknown[] {
        const directAliases = Array.isArray(option.valueAliases) ? option.valueAliases : [];

        if (option.raw === null || typeof option.raw !== 'object') {
            return directAliases;
        }

        const rawAliases = (option.raw as Record<string, unknown>)['valueAliases'];

        return Array.isArray(rawAliases) ? [...directAliases, ...rawAliases] : directAliases;
    }

    public static extractComparableId(
        value: unknown,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): string | number | null {
        return ReactiveFormValueUtils.extractComparableIds(value, primaryKey, identityKeys)[0] ?? null;
    }

    private static extractComparableIds(
        value: unknown,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): Array<string | number> {
        if (typeof value === 'number') {
            return [value];
        }

        if (typeof value === 'string') {
            return ReactiveFormValueUtils.isBadDisplayToken(value) ? [] : [value];
        }

        if (typeof value !== 'object' || value === null) {
            return [];
        }

        const record = value as Record<string, unknown>;
        const keys = ReactiveFormValueUtils.toUniqueIdentityKeys([primaryKey, ...identityKeys, DEFAULT_DROPDOWN_IDENTITY_KEY]);
        const comparableIds: Array<string | number> = [];

        for (const key of keys) {
            const candidate = record[key];

            if (typeof candidate === 'number') {
                comparableIds.push(candidate);
            }

            if (typeof candidate === 'string' && !ReactiveFormValueUtils.isBadDisplayToken(candidate)) {
                comparableIds.push(candidate);
            }
        }

        return comparableIds;
    }

    public static remapValueWithOptions(
        value: unknown,
        options: ReactiveDropdownOption[],
        primaryKey: string,
        isMulti: boolean,
        externalDropdownsReady: boolean,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
        identityKeys: readonly string[] = [],
    ): unknown {
        if (isMulti) {
            return ReactiveFormValueUtils.remapMultiValueWithOptions(
                value,
                options,
                primaryKey,
                externalDropdownsReady,
                optionsIndex,
                identityKeys,
            );
        }

        return ReactiveFormValueUtils.remapSingleValueWithOptions(
            value,
            options,
            primaryKey,
            externalDropdownsReady,
            optionsIndex,
            identityKeys,
        );
    }

    public static areValuesEquivalent(
        first: unknown,
        second: unknown,
        primaryKey = DEFAULT_DROPDOWN_IDENTITY_KEY,
        identityKeys: readonly string[] = [],
    ): boolean {
        if (first === second) {
            return true;
        }

        if (first === null || first === undefined || second === null || second === undefined) {
            return ReactiveFormValueUtils.isEmptyComparableValue(first) && ReactiveFormValueUtils.isEmptyComparableValue(second);
        }

        if (Array.isArray(first) || Array.isArray(second)) {
            if (!Array.isArray(first) || !Array.isArray(second) || first.length !== second.length) {
                return false;
            }

            const firstComparable = first
                .map((item) => ReactiveFormValueUtils.toComparableValue(item, primaryKey, identityKeys))
                .sort();
            const secondComparable = second
                .map((item) => ReactiveFormValueUtils.toComparableValue(item, primaryKey, identityKeys))
                .sort();

            return firstComparable.every((item, index) => item === secondComparable[index]);
        }

        return ReactiveFormValueUtils.toComparableValue(first, primaryKey, identityKeys)
            === ReactiveFormValueUtils.toComparableValue(second, primaryKey, identityKeys);
    }

    public static areModelSnapshotsEquivalent(first: Record<string, unknown>, second: Record<string, unknown>): boolean {
        const firstKeys = Object.keys(first ?? {});
        const secondKeys = Object.keys(second ?? {});

        if (firstKeys.length !== secondKeys.length) {
            return false;
        }

        for (const key of firstKeys) {
            if (!ReactiveFormValueUtils.areValuesEquivalent(first[key], second[key])) {
                return false;
            }
        }

        return true;
    }

    public static formatPreviewValue<T>(value: unknown, field: FormField<T>): string {
        if (Array.isArray(value)) {
            const parts = value
                .map((item) => ReactiveFormValueUtils.toDisplayText(item, field))
                .filter((item) => item.length > 0);

            if (parts.length === 0) {
                return '';
            }

            const visibleParts = parts.slice(0, 3);
            const hiddenItems = parts.length - visibleParts.length;

            return hiddenItems > 0 ? `${visibleParts.join(', ')} +${hiddenItems}` : visibleParts.join(', ');
        }

        return ReactiveFormValueUtils.toDisplayText(value, field);
    }

    public static splitTwoFieldsConcat(value: string, separator: string): [string, string] {
        if (!value) {
            return ['', ''];
        }

        const normalizedSeparator = (separator ?? '').trim();

        if (!normalizedSeparator) {
            return [value, ''];
        }

        const separatorIndex = value.indexOf(normalizedSeparator);

        if (separatorIndex < 0) {
            return [value, ''];
        }

        const leftValue = value.slice(0, separatorIndex).trim();
        const rightValue = value.slice(separatorIndex + normalizedSeparator.length).trim();

        return [leftValue, rightValue];
    }


    private static isEmptyComparableValue(value: unknown): boolean {
        return value === null
            || value === undefined
            || (typeof value === 'string' && ReactiveFormValueUtils.isBadDisplayToken(value));
    }

    private static toComparableValue(value: unknown, primaryKey: string, identityKeys: readonly string[] = []): string {
        const comparableId = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);

        if (comparableId !== null) {
            return String(comparableId);
        }

        if (value instanceof Date) {
            return value.toISOString();
        }

        if (typeof value === 'object' && value !== null) {
            return ReactiveFormValueUtils.stringifyComparableObject(value);
        }

        return String(value ?? '');
    }

    private static stringifyComparableObject(value: object): string {
        const entries = Object.entries(value as Record<string, unknown>)
            .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
            .map(([key, item]) => [key, ReactiveFormValueUtils.toComparableValue(item, 'id')]);

        return JSON.stringify(entries);
    }

    private static remapMultiValueWithOptions(
        value: unknown,
        options: ReactiveDropdownOption[],
        primaryKey: string,
        externalDropdownsReady: boolean,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
        identityKeys: readonly string[] = [],
    ): unknown {
        const values = Array.isArray(value) ? value : [];
        const ids = values
            .map((item) => ReactiveFormValueUtils.extractComparableId(item, primaryKey, identityKeys))
            .filter((item): item is string | number => item !== null);
        const mappedValues = ids
            .map((id) => ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey, optionsIndex, identityKeys))
            .filter((item) => item !== null);

        if (mappedValues.length > 0) {
            return mappedValues;
        }

        if (ids.length > 0) {
            return ids;
        }

        return externalDropdownsReady ? [] : values;
    }

    private static remapSingleValueWithOptions(
        value: unknown,
        options: ReactiveDropdownOption[],
        primaryKey: string,
        externalDropdownsReady: boolean,
        optionsIndex?: ReadonlyMap<string, ReactiveDropdownOption>,
        identityKeys: readonly string[] = [],
    ): unknown {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string' && ReactiveFormValueUtils.isBadDisplayToken(value)) {
            return null;
        }

        const id = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);
        const mappedValue = id === null
            ? null
            : ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey, optionsIndex, identityKeys);

        if (mappedValue !== null) {
            return mappedValue;
        }

        if (ReactiveFormValueUtils.hasNonEmptyValue(value)) {
            return value;
        }

        return externalDropdownsReady ? null : value;
    }

    private static toDisplayText<T>(value: unknown, field: FormField<T>): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string') {
            return ReactiveFormValueUtils.isBadDisplayToken(value) ? '' : value;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (typeof value === 'object') {
            const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);
            const record = value as Record<string, unknown>;
            const candidates: unknown[] = [
                record['label'],
                record['nombre'],
                record['name'],
                record['razon_social'],
                record['descripcion'],
                record[primaryKey],
                record['id'],
            ];

            for (const candidate of candidates) {
                if (typeof candidate === 'string' && !ReactiveFormValueUtils.isBadDisplayToken(candidate) && candidate.trim().length > 0) {
                    return candidate.trim();
                }

                if (typeof candidate === 'number') {
                    return String(candidate);
                }
            }
        }

        return '';
    }
}
